import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  ArrowRight,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Calendar,
  Wallet,
  Target,
  Percent,
  Plus
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { dashboardApi } from '../utils/api';
import useAuthStore from '../store/authStore';
import EmptyState from '../components/EmptyState';
import { LoadingPage } from '../components/LoadingSpinner';

// AI Dashboard Components
import {
  KPICard,
  CashFlowChart,
  SmartAlertsCarousel,
  ClientRiskTable,
  SupplierPaymentList,
  AnomalyAlerts
} from '../components/dashboard';

export default function Dashboard() {
  const { currentEmisor, user, tipoCambio } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);

  // AI Dashboard state
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);

  useEffect(() => {
    if (currentEmisor) {
      loadData();
      loadAIData();
    }
  }, [currentEmisor]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, docsRes, monthlyRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getRecentDocuments(),
        dashboardApi.getMonthlyStats(6)
      ]);

      setStats(statsRes.data.data);
      setRecentDocs(docsRes.data.data || []);
      setMonthlyData(monthlyRes.data.data || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAIData = async () => {
    setAiLoading(true);
    try {
      const response = await dashboardApi.getAISummary();
      setAiData(response.data.data);
    } catch (error) {
      console.error('Failed to load AI dashboard data:', error);
    } finally {
      setAiLoading(false);
    }
  };

  if (!currentEmisor) {
    return (
      <EmptyState
        icon={FileText}
        title="Seleccione una empresa"
        description="Use el selector en la parte superior para elegir la empresa con la que desea trabajar."
      />
    );
  }

  if (loading) {
    return <LoadingPage />;
  }

  const formatCurrency = (amount, currency = 'USD') => {
    const symbol = currency === 'USD' ? '$' : '';
    if (currency === 'CRC') {
      return new Intl.NumberFormat('es-CR', {
        style: 'currency',
        currency: 'CRC',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0);
    }
    return `${symbol}${new Intl.NumberFormat('es-CR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0)}`;
  };

  return (
    <div className="space-y-6">
      {/* Smart Alerts Carousel with Exchange Rate */}
      <SmartAlertsCarousel
        alerts={aiData?.alerts || []}
        loading={aiLoading}
        tipoCambio={tipoCambio}
      />

      {/* ==================== */}
      {/* FACTURACION SECTION */}
      {/* ==================== */}

      {/* Financial Summary - Totales Facturados */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Total USD */}
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-200">
              <DollarSign className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-emerald-600 tracking-wide">Total Facturado (USD)</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.totalFacturadoUSD || 0, 'USD')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
            <span>Este mes • Facturas en dolares</span>
          </div>
        </div>

        {/* Total CRC */}
        <div className="bg-white border-2 border-blue-200 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center border border-blue-200">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-blue-600 tracking-wide">Total Facturado (CRC)</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.totalFacturadoCRC || 0, 'CRC')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <Calendar className="h-3.5 w-3.5 text-blue-500" />
            <span>Este mes • Facturas en colones</span>
          </div>
        </div>
      </div>

      {/* Grafico Facturas por Mes con Stats integrados */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        {/* Header con stats inline */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-200">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Facturacion Este Mes</h2>
              <p className="text-xs text-gray-500">Resumen y tendencia de los ultimos 6 meses</p>
            </div>
          </div>

          {/* Stats inline */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="text-xs text-gray-500">Total:</span>
              <span className="text-sm font-bold text-gray-900">{stats?.total || 0}</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-sm font-bold text-emerald-700">{stats?.accepted || 0}</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
              <Clock className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-sm font-bold text-amber-700">{(stats?.pending || 0) + (stats?.draft || 0)}</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
              <XCircle className="h-3.5 w-3.5 text-red-600" />
              <span className="text-sm font-bold text-red-700">{stats?.rejected || 0}</span>
            </div>
            <button
              onClick={() => { loadData(); loadAIData(); }}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorAceptadas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorPendientes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRechazadas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorBorradores" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} tickLine={false} />
              <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Area
                type="monotone"
                dataKey="aceptadas"
                stroke="#10b981"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorAceptadas)"
                name="Aceptadas"
              />
              <Area
                type="monotone"
                dataKey="borradores"
                stroke="#f59e0b"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorBorradores)"
                name="Borradores"
              />
              <Area
                type="monotone"
                dataKey="rechazadas"
                stroke="#ef4444"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorRechazadas)"
                name="Rechazadas"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Leyenda */}
        <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span className="text-xs text-gray-600">Aceptadas</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span className="text-xs text-gray-600">Borradores</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-xs text-gray-600">Rechazadas</span>
          </div>
        </div>
      </div>

      {/* ==================== */}
      {/* AI ANALYTICS SECTION */}
      {/* ==================== */}

      {/* Section Header */}
      <div className="flex items-center gap-2 pt-2">
        <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center">
          <Target className="h-4 w-4 text-purple-600" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Analitica Inteligente</h2>
          <p className="text-xs text-gray-500">Proyecciones, riesgos y KPIs financieros</p>
        </div>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          titulo="DSO"
          valor={aiData?.kpis?.dso?.valor || 0}
          valorAnterior={aiData?.kpis?.dso?.valorAnterior}
          tendencia={aiData?.kpis?.dso?.tendencia}
          benchmark={aiData?.kpis?.dso?.benchmark}
          descripcion={aiData?.kpis?.dso?.descripcion}
          formato="dias"
          icono={Clock}
          colorIcono="blue"
        />
        <KPICard
          titulo="DPO"
          valor={aiData?.kpis?.dpo?.valor || 0}
          valorAnterior={aiData?.kpis?.dpo?.valorAnterior}
          tendencia={aiData?.kpis?.dpo?.tendencia}
          benchmark={aiData?.kpis?.dpo?.benchmark}
          descripcion={aiData?.kpis?.dpo?.descripcion}
          formato="dias"
          icono={Calendar}
          colorIcono="purple"
        />
        <KPICard
          titulo="Eficiencia Cobro"
          valor={aiData?.kpis?.eficienciaCobro?.valor || 0}
          valorAnterior={aiData?.kpis?.eficienciaCobro?.valorAnterior}
          tendencia={aiData?.kpis?.eficienciaCobro?.tendencia}
          benchmark={aiData?.kpis?.eficienciaCobro?.benchmark}
          descripcion={aiData?.kpis?.eficienciaCobro?.descripcion}
          formato="porcentaje"
          icono={Percent}
          colorIcono="emerald"
        />
        <KPICard
          titulo="Capital Trabajo"
          valor={aiData?.kpis?.capitalTrabajo?.valor || 0}
          valorAnterior={aiData?.kpis?.capitalTrabajo?.valorAnterior}
          tendencia={aiData?.kpis?.capitalTrabajo?.tendencia}
          descripcion={aiData?.kpis?.capitalTrabajo?.descripcion}
          formato={aiData?.kpis?.capitalTrabajo?.moneda === 'USD' ? 'moneda_usd' : 'moneda'}
          icono={Wallet}
          colorIcono="amber"
        />
      </div>

      {/* Cash Flow Projection — anchor para "Ver Proyección" desde alertas */}
      <div id="cash-flow" className="scroll-mt-32">
        <CashFlowChart
          data={aiData?.cashFlow}
          loading={aiLoading}
        />
      </div>

      {/* Client Risk & Supplier Payments Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Anchor para "Ver Análisis" desde alertas */}
        <div id="client-risk" className="scroll-mt-32">
          <ClientRiskTable
            data={aiData?.clientRisk}
            loading={aiLoading}
          />
        </div>
        <SupplierPaymentList
          data={aiData?.supplierAnalysis}
          loading={aiLoading}
        />
      </div>

      {/* Anomalies & Recent Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Anomaly Detection */}
        <AnomalyAlerts
          anomalies={aiData?.anomalies || []}
          loading={aiLoading}
        />

        {/* Recent Documents */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                <FileText className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Actividad Reciente</h2>
                <p className="text-xs text-gray-500">Ultimos documentos procesados</p>
              </div>
            </div>
          </div>

          {recentDocs.length === 0 ? (
            <div className="p-8 text-center">
              <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3 border border-gray-200">
                <FileText className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">No hay documentos aun</h3>
              <p className="text-xs text-gray-500 mb-3">Crea tu primera factura electronica</p>
              <Link
                to="/facturacion/nueva"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-blue-400 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-50 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Crear Factura
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentDocs.slice(0, 5).map((doc) => {
                const statusConfig = {
                  accepted: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Aceptada' },
                  rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Rechazada' },
                  pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Pendiente' },
                  processing: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Procesando' },
                  draft: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', label: 'Borrador' },
                  error: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Error' }
                };
                const status = statusConfig[doc.status] || statusConfig.draft;

                return (
                  <Link
                    key={doc.id}
                    to={`/facturacion/historial`}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {doc.consecutivo}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {doc.receptor_nombre || doc.receptorNombre || 'Sin receptor'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(doc.total_comprobante || doc.totales?.totalComprobante || 0, doc.codigo_moneda || 'USD')}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${status.bg} ${status.text} ${status.border}`}>
                        {status.label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {recentDocs.length > 0 && (
            <div className="p-3 bg-gray-50 border-t border-gray-100">
              <Link
                to="/facturacion/historial"
                className="flex items-center justify-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                Ver todos los documentos
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
